import { useEffect, useRef, useState } from 'react';
import { useAutonomyProfiles } from '../api/queries';
import type { Lang } from '../api/types';
import { useT } from '../i18n';
import { useLive } from '../live/store';
import { setAutonomyParams } from '../lib/autonomy';
import { HeadPanel, LightControl, VolumeControl } from './RobotControls';
import { HandPushButton } from './HandPush';

const LANGS: Lang[] = ['uz', 'ru', 'en'];
const isLang = (v: unknown): v is Lang => v === 'uz' || v === 'ru' || v === 'en';

/**
 * Slider that shows the server value, keeps the operator's value while dragging and until the status catches up,
 * and sends it debounced (300 ms).
 */
function LiveSlider({
  label,
  title,
  value,
  min,
  max,
  step,
  format,
  disabled,
  onSend,
}: {
  label: string;
  title?: string;
  value: number | null;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  disabled?: boolean;
  onSend: (v: number) => Promise<boolean>;
}) {
  const [draft, setDraft] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settle = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (settle.current) clearTimeout(settle.current);
    },
    [],
  );
  const shown = draft ?? value ?? min;
  const change = (v: number) => {
    setDraft(v);
    if (timer.current) clearTimeout(timer.current);
    if (settle.current) clearTimeout(settle.current);
    timer.current = setTimeout(() => {
      void onSend(v).then((ok) => {
        if (!ok) setDraft(null);
        else settle.current = setTimeout(() => setDraft(null), 1500);
      });
    }, 300);
  };
  return (
    <label className="range-field" title={title}>
      <span className="top">
        <span>{label}</span>
        <span className="val">{value === null && draft === null ? '—' : format(shown)}</span>
      </span>
      <input type="range" min={min} max={max} step={step} value={shown} disabled={disabled} aria-label={label} aria-valuetext={format(shown)} onChange={(e) => change(Number(e.target.value))} />
      <span className="ends">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </span>
    </label>
  );
}

/** "Controls" tab next to the live transcript: adjustments that do not pause autonomy. Mic mode stays in the header. */
export function AutonomyLiveControls({ serial, online }: { serial: string; online: boolean }) {
  const t = useT();
  const auto = useLive((s) => s.robots[serial]?.status?.autonomy);
  const log = useLive((s) => s.autonomyLog[serial]);
  const profiles = useAutonomyProfiles();
  const profile = profiles.data?.find((p) => p.id === auto?.profileId);
  const [langDraft, setLangDraft] = useState<Lang | null>(null);

  const maxSpeed = typeof auto?.maxSpeed === 'number' ? auto.maxSpeed : (profile?.maxSpeed ?? null);
  const turnSpeed = typeof auto?.turnSpeed === 'number' ? auto.turnSpeed : null;
  let lang: Lang | null = isLang(auto?.lang) ? auto.lang : null;
  if (!lang && log)
    for (let i = log.length - 1; i >= 0 && !lang; i--) {
      const e = log[i];
      if (e && e.kind === 'state' && isLang(e.lang)) lang = e.lang;
    }
  if (!lang && profile && isLang(profile.defaultLang)) lang = profile.defaultLang;
  const langShown = langDraft ?? lang;

  const pickLang = async (l: Lang) => {
    if (l === langShown) return;
    setLangDraft(l);
    const ok = await setAutonomyParams(serial, { lang: l });
    setTimeout(() => setLangDraft(null), ok ? 1500 : 0);
  };

  return (
    <div className="auto-controls">
      <section className="panel" style={{ gap: 14 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 className="h2">{t('auto.sessionNow')}</h2>
          <span className="muted" style={{ fontSize: 11 }}>
            {t('auto.controlsNote')}
          </span>
        </div>
        <LiveSlider
          label={t('auto.maxSpeedSession')}
          value={maxSpeed}
          min={0.1}
          max={0.5}
          step={0.05}
          format={(v) => `${v.toFixed(2)} m/s`}
          disabled={!online}
          onSend={(v) => setAutonomyParams(serial, { maxSpeed: Math.round(v * 100) / 100 })}
        />
        <LiveSlider
          label={t('auto.turnSpeed')}
          title={t('auto.turnSpeedTip')}
          value={turnSpeed}
          min={10}
          max={90}
          step={5}
          format={(v) => `${Math.round(v)}°/s`}
          disabled={!online}
          onSend={(v) => setAutonomyParams(serial, { turnSpeed: Math.round(v) })}
        />
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13 }}>{t('auto.sessionLang')}</span>
          <div role="radiogroup" aria-label={t('auto.sessionLang')} className="seg sm">
            {LANGS.map((l) => (
              <button key={l} type="button" role="radio" aria-checked={langShown === l} disabled={!online} onClick={() => void pickLang(l)}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </section>
      <section className="panel" style={{ gap: 12 }}>
        <VolumeControl serial={serial} online={online} />
        <LightControl serial={serial} online={online} compact />
      </section>
      <HeadPanel serial={serial} online={online} />
      <section className="panel" style={{ gap: 8 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13 }}>{t('hand.title')}</span>
          <HandPushButton serial={serial} online={online} className="sm" />
        </div>
      </section>
    </div>
  );
}
